import React, { useState } from 'react';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import { type FormikProps } from 'formik';
import Loading from '@components/Shared/Loader/Loading';
import useGetOnhandInventoryItemsByInventoryId from '@hooks/INV/MasterItems/useGetOnhandInventoryItemsByInventoryId';
import { useParams } from 'react-router-dom';
import { useAppDispatch } from '@store/hooks';
import {
  deleteMaterialTransactionsAllocateLine,
  addMaterialTransactionsAllocateLine,
  getAllocateTransactionsById
} from '@store/INV/Transactions/InvAllocate/InvAllocateSlice';
import CustomAlert from '@components/Shared/Alert/CustomAlert';
import confirmAction from '@components/Shared/Alert/confirmAction';
import { TreeSelect } from 'antd';
import useGetSubinventoryList from '@hooks/INV/Inventories/useGetSubinventoryList';
import PopoverCustom from '@components/Shared/Popover/PopoverCustom';

interface LinesProps {
  formik: FormikProps<any>;
  isUpdate?: boolean;
}

const AllocateLines: React.FC<LinesProps> = ({ formik, isUpdate = false }) => {
  const { values, touched, errors } = formik;
  const { id } = useParams();
  const { TreeSubInventoryData, loading: loading1, error: error1 } = useGetSubinventoryList();

  // const [isLoading, setIsLoading] = useState(false);
  const [rowLoading, setRowLoading] = useState<{ [key: number]: boolean }>({});

  const dispatch = useAppDispatch();
  const { i18n, t } = useTranslation();
  const { InventoryItemsOptions, loading, error } = useGetOnhandInventoryItemsByInventoryId({
    InventoryId: values.inventoryId,
    SubInventoryId: values.subInventoryId
  });

  // const handleLineChange = (index: number, field: string, value: any) => {
  //   const updatedLines = [...values.materialTransactionsLineList];

  //   if (field === 'transactionItemId') {
  //     // const unitPrice = InventoryItemsOptions?.find((f) => f.value === value)?.purchasePrice || 0;
  //     const transactionUom = InventoryItemsOptions?.find(f => f.itemId === value && f.isPrimaryUOM)?.uom;

  //     updatedLines[index] = {
  //       ...updatedLines[index],
  //       [field]: value,
  //       transactionUom,
  //       unitPrice: Number(0),
  //       total: Number(updatedLines[index].transactionQuantityIn) * Number(0)
  //     };
  //   } else {
  //     updatedLines[index] = {
  //       ...updatedLines[index],
  //       [field]: value
  //     };
  //   }

  //   formik.setFieldValue('materialTransactionsLineList', updatedLines);
  // };

  const handleLineChange = (index: number, field: string, value: any) => {
    const updatedLines = [...values.materialTransactionsLineList];

    if (field === 'lotId') {
      updatedLines[index] = {
        ...updatedLines[index],
        transactionQuantityIn: 0
      };
    }
    if (field === 'transactionItemId') {
      const transactionUom = InventoryItemsOptions?.find(f => f.itemId === value && f.isPrimaryUOM)?.uom;

      updatedLines[index] = {
        ...updatedLines[index],
        [field]: value,
        transactionUom,
        unitPrice: Number(0),
        total: Number(updatedLines[index].transactionQuantityIn) * Number(0)
      };
    } else {
      updatedLines[index] = {
        ...updatedLines[index],
        [field]: value
      };
    }

    formik.setFieldValue('materialTransactionsLineList', updatedLines);
  };

  const handleAddLine = () => {
    const newLine: any = {
      // lineNumber: Lines?.length + 1,
      id: null, //
      transactionItemId: null,
      transactionUom: '',
      // unitPrice: 0,
      transactionQuantityIn: 0,
      // transactionQuantityOut: 0,
      lotId: null
      //________________________
      // inventoryId: '',
      // discount: 0,
      // codeCombinationId: '',
      // currencyCode: '',
      // currencyConversionRate: 0,
      // referenceIdMaster: '',
      // referenceIdLine: '',
      // transactionTypeId: '',
      // equivalent: 0,
      // expensesAmount: 0,
      // batchId: ''
    };

    const currentLines = formik.values.materialTransactionsLineList || []; // تأكد من أن القائمة موجودة كمصفوفة
    formik.setFieldValue('materialTransactionsLineList', [...currentLines, newLine]);
  };

  const handleDeleteLine = (index: number) => {
    const updatedLines = values.materialTransactionsLineList?.filter((_: any, i: number) => i !== index);
    formik?.setFieldValue('materialTransactionsLineList', updatedLines);
  };

  const handleDeleteAllocateLine = async (line: any, index: number) => {
    if (!id) {
      console.error('Transaction ID is undefined');
      return;
    }
    const confrim = await confirmAction('هل أنت متأكد من فك حجز هذا الصنف ؟');
    if (!confrim) {
      return;
    }
    // setIsLoading(true); // تعطيل الزر
    setRowLoading(prev => ({ ...prev, [index]: true })); // تعطيل الزر للصف المحدد
    try {
      const res = await dispatch(
        deleteMaterialTransactionsAllocateLine({ transactionId: id, transactionLineId: line.id })
      ).unwrap();
      if (res.succeeded && res.data) {
        CustomAlert({ action: 'Add', msg: 'تمت العملية بنجاح' });
        const updatedLines = values.materialTransactionsLineList?.filter((f: any) => f.id !== line.id);
        formik.setFieldValue('materialTransactionsLineList', updatedLines);
      } else {
        CustomAlert({ action: 'Error', msg: res.message || 'فشلت العملية!' });
      }
    } catch (error) {
      CustomAlert({ action: 'Error', msg: 'حدث خطأ أثناء فك الحجز!' });
    } finally {
      setRowLoading(prev => ({ ...prev, [index]: false }));
    }
  };

  const handleAddAllocateLine = async (line: any, index: number) => {
    if (!id) {
      console.error('Transaction ID is undefined');
      return;
    }

    if (!line.transactionItemId || !line.transactionQuantityIn || !line.transactionUom) {
      CustomAlert({ action: 'info', msg: 'أكمل إدخال البيانات' });
      return;
    }
    // setIsLoading(true); // تعطيل الزر
    setRowLoading(prev => ({ ...prev, [index]: true })); // تعطيل الزر للصف المحدد
    try {
      const res = await dispatch(
        addMaterialTransactionsAllocateLine({
          transactionId: id,
          transactionItemId: line.transactionItemId,
          transactionUom: line.transactionUom,
          transactionQuantityIn: line.transactionQuantityIn,
          lotId: line.lotId
        })
      ).unwrap();

      if (res.succeeded && res.data) {
        CustomAlert({ action: 'Add', msg: 'تمت العملية بنجاح' });
        dispatch(getAllocateTransactionsById(id));
      } else {
        CustomAlert({ action: 'Error', msg: res.message || 'فشلت العملية!' });
      }
    } catch (error) {
      CustomAlert({ action: 'Error', msg: 'حدث خطأ أثناء الحجز!' });
    } finally {
      setRowLoading(prev => ({ ...prev, [index]: false }));
    }
  };

  return (
    <>
      <div className='row'>
        <div className='col-lg-4'>
          <div className='input-group flex-nowrap'>
            <span className='input-group-text  fw-bold bg-theme bg-opacity-30'>{t('المخزن الفرعي')}</span>
            <TreeSelect
              loading={loading1}
              showSearch
              className='w-100'
              treeLine
              placeholder={i18n.language === 'ar' ? 'إختر' : 'Choose'}
              allowClear
              treeDefaultExpandAll={false}
              value={values.subInventoryId}
              onChange={(option: any) => {
                formik.setFieldValue('subInventoryId', option === null ? null : option);
              }}
              treeData={TreeSubInventoryData?.filter(f => f.parentId === values.inventoryId)}
              treeNodeFilterProp='title'
            />
          </div>
        </div>

        <div className='col-lg-8 text-end  '>
          <div className='btn-group mb-3'>
            <button type='button' className='btn btn-theme' onClick={handleAddLine}>
              {t('حجز صنف جديــد')}
            </button>
          </div>
        </div>
      </div>
      <Loading loading={loading} error={error} Type='Dots'>
        <div
          className={`table-responsive rounded-2 text-center ${
            formik.errors.materialTransactionsLineList ? 'border-bottom border-2 border-danger' : ''
          } `}
        >
          <div
            style={{
              maxHeight: '350px',
              overflowY: 'auto',
              overflowX: 'auto',
              position: 'relative'
            }}
          >
            <table className='table table-borderless  table-hover mb-0 '>
              <thead className='table-dark   sticky-top shadow'>
                <tr>
                  <th className='align-middle' style={{ width: '3%' }}>
                    #
                  </th>
                  <th className='align-middle' style={{ width: '40%' }}>
                    {t('اسم الصنف')}
                  </th>
                  <th className='align-middle' style={{ width: '15%' }}>
                    {t('الوحدة')}
                  </th>
                  <th className='align-middle' style={{ width: '15%' }}>
                    {t('رقم التشغيـل')}
                  </th>
                  <th className='align-middle' style={{ width: '8%' }}>
                    {t('الكمية')}
                  </th>

                  {!isUpdate && <th className='align-middle text-end' style={{ width: '5%' }}></th>}
                  {isUpdate && <th className='align-middle text-end' style={{ width: '11%' }}></th>}
                </tr>
              </thead>

              <tbody className='fw-bold'>
                {values.materialTransactionsLineList
                  ?.slice()
                  ?.reverse()
                  ?.map((line: any, index: any) => {
                    const Index = values.materialTransactionsLineList?.length - 1 - index;

                    const ItemDetails = InventoryItemsOptions?.find(
                      f => f.itemId === line.transactionItemId && f.uom === line.transactionUom
                    );

                    const availableQty = ItemDetails?.AvailableQty ?? 0;

                    const selectedLot = ItemDetails?.lotAvlQtys?.find(f => f.lotId === line.lotId);

                    const optionsLots = ItemDetails?.lotAvlQtys?.map(lot => ({
                      value: lot.lotId,
                      label: lot.lotNumber,
                      expDate: lot.expirationDate
                    }));

                    const selectedItems = values.materialTransactionsLineList
                      ?.filter((_: any, idx: any) => idx !== Index)
                      ?.map((line: any) => line.transactionItemId)
                      ?.filter(Boolean);
                    return (
                      <tr key={index}>
                        <td className='align-middle bg-theme bg-opacity-30 small'>
                          {values.materialTransactionsLineList?.length - index}
                        </td>
                        <td
                          className='align-middle small'
                          title={InventoryItemsOptions?.find(f => f.itemId === line.transactionItemId)?.itemName || ''}
                        >
                          {/* <Select
                            isDisabled={line.id !== null}
                            classNamePrefix='react-select'
                            className='w-100'
                            isSearchable
                            isClearable
                            options={InventoryItemsOptions?.filter(
                              (item, index, self) =>
                                index === self?.findIndex(i => i.itemId === item.itemId) &&
                                !selectedItems.includes(item.itemId) // استبعاد العناصر المختارة مسبقًا
                            )}
                            onChange={option =>
                              handleLineChange(Index, 'transactionItemId', option === null ? null : option.value)
                            }
                            value={
                              line.transactionItemId === null
                                ? null
                                : InventoryItemsOptions?.find(option => option.value === line.transactionItemId)
                            }
                            placeholder={i18n.language === 'ar' ? 'إختر' : 'Choose'}
                            menuPortalTarget={document.body}
                            styles={{
                              menuPortal: (base: any) => ({ ...base, zIndex: 9999 })
                            }}
                            formatOptionLabel={option => (
                              <div className='d-flex justify-content-between ' title={option.label}>
                                <div className=''>{option.label}</div>
                                <div className='text-theme fw-bold'>
                                  {option?.qtyByLot?.reduce((sum, item) => sum + item.qty, 0) ?? 0}
                                </div>
                              </div>
                            )}
                          /> */}

                          <Select
                            classNamePrefix='react-select'
                            className='w-100'
                            isSearchable
                            isClearable
                            options={InventoryItemsOptions?.filter(
                              (item, index, self) =>
                                index === self?.findIndex(i => i.itemId === item.itemId) &&
                                !selectedItems.includes(item.itemId)
                            )}
                            onChange={option =>
                              handleLineChange(Index, 'transactionItemId', option === null ? null : option.value)
                            }
                            value={
                              line.transactionItemId === null
                                ? null
                                : InventoryItemsOptions?.find(option => option.value === line.transactionItemId)
                            }
                            placeholder={t('Choose')}
                            menuPortalTarget={document.body}
                            styles={{
                              menuPortal: (base: any) => ({ ...base, zIndex: 9999 })
                            }}
                            formatOptionLabel={option => (
                              <div className='d-flex justify-content-between   '>
                                <div className=''>{option.label}</div>
                                <div className='text-bode fw-bold'>
                                  {option.lotAvlQtys?.reduce((sum, item) => sum + item.qty, 0) ?? 0}
                                </div>
                              </div>
                            )}
                          />
                        </td>

                        <td className='align-middle small'>
                          <select
                            disabled={line.id !== null}
                            className='form-select text-center'
                            value={line.transactionUom || ''}
                            onChange={e => handleLineChange(Index, 'transactionUom', e.target.value)}
                          >
                            <option value=''>{t('Choose')}</option>
                            {InventoryItemsOptions?.filter(f => f.itemId === line.transactionItemId)?.map(
                              (item, idx) => (
                                <option key={++idx} value={item.uom}>
                                  {item.uomName}
                                </option>
                              )
                            )}
                          </select>
                        </td>

                        <td className='align-middle small'>
                          <Select
                            classNamePrefix='react-select'
                            className='w-100  '
                            isSearchable
                            isClearable
                            options={optionsLots}
                            onChange={option => handleLineChange(Index, 'lotId', option === null ? null : option.value)}
                            // value={line.lotId === null ? null : optionsLots?.find(option => option.value === line.lotId)}
                            value={
                              line.lotId
                                ? optionsLots?.find(option => option.value === line.lotId)
                                : optionsLots && optionsLots.length > 0
                                ? optionsLots[0] // الافتراضي أول خيار
                                : null
                            }
                            placeholder={t('AllLots')}
                            menuPortalTarget={document.body}
                            styles={{
                              menuPortal: (base: any) => ({ ...base, zIndex: 9999 })
                            }}
                            formatOptionLabel={option => {
                              const lotDetails = ItemDetails?.lotAvlQtys?.find(f => f.lotId === option.value);

                              return (
                                <div className='d-flex justify-content-between align-items-center p-0'>
                                  <div className='d-flex flex-column text-start p-0'>
                                    {lotDetails?.lotId === null ? (
                                      <div className='text-dangerZ  '>{t('All')}</div>
                                    ) : (
                                      <>
                                        <div className='fw-bold'>{option.label}</div>
                                        <div className='text-themeZ '>{option.expDate}</div>
                                      </>
                                    )}
                                  </div>

                                  <div className='text-themeZ  p-0'>
                                    {lotDetails?.lotId === null
                                      ? ItemDetails?.lotAvlQtys?.reduce((sum, item) => sum + item.qty, 0) ?? 0
                                      : lotDetails?.qty ?? 0}
                                  </div>
                                </div>
                              );
                            }}
                          />
                        </td>

                        {/* <td className='align-middle small'>
                          <PopoverCustom
                            childern={
                              <input
                                type='text'
                                autoComplete='off'
                                className='form-control text-center px-0'
                                value={line.transactionQuantityIn}
                                onChange={e => handleLineChange(Index, 'transactionQuantityIn', Number(e.target.value))}
                                // onInput={e => {
                                //   const input = e.target as HTMLInputElement;
                                //   input.value = input.value?.replace(/[^0-9]/g, '');
                                //   if (Number(input.value) > SumQtyInStore) {
                                //     input.value = SumQtyInStore?.toString();
                                //   }
                                // }}
                              />
                            }
                            // Header={`الكمية في المخزن : ${SumQtyInStore}`}
                            Body={
                              <>
                                <div className='d-flex justify-content-between'>
                                  <span className='mt-1'>الكمية في المخزن</span>
                                  <span className='text-theme fw-bold fs-6 mx-2 mt-1'>{Number(SumQtyInStore)}</span>
                                </div>
                              </>
                            }
                          />
                        </td> */}

                        <td className='align-middle small'>
                          <PopoverCustom
                            childern={
                              <input
                                type='number'
                                autoComplete='off'
                                className='form-control text-center px-0'
                                value={line.transactionQuantityIn}
                                onChange={e => handleLineChange(Index, 'transactionQuantityIn', Number(e.target.value))}
                                // السابق
                                onInput={e => {
                                  // debugger;
                                  const input = e.target as HTMLInputElement;
                                  input.value = input.value?.replace(/[^0-9]/g, '');

                                  const lotQty =
                                    selectedLot && selectedLot.lotId != null
                                      ? selectedLot.qty ?? 0
                                      : ItemDetails?.lotAvlQtys?.reduce((sum, item) => sum + item.qty, 0) ?? 0;

                                  const remainingQty = line.RemainingQty;
                                  const minQty = remainingQty !== undefined ? Math.min(lotQty, remainingQty) : lotQty;

                                  const enteredQty = Number(input.value);

                                  if (enteredQty > minQty) {
                                    input.value = minQty.toString();
                                  }
                                }}
                              />
                            }
                            // Header={`${t('AvailableQuantity')} : ${selectedLot?.qty ?? 0}`}
                            Header={`${t('AvailableQuantity')} : ${
                              (selectedLot?.qty ?? 0) + Number(line.RemainingQty || 0)
                            }`}
                            Body={
                              <>
                                <div className='d-flex justify-content-between'>
                                  <span className='mt-1'>الكمية في المخزن</span>

                                  <span className='text-theme fw-bold fs-6 mx-2 mt-1'>
                                    {`  ${selectedLot?.qty ?? 0}`}{' '}
                                  </span>
                                </div>
                              </>
                            }
                          />
                        </td>

                        {!isUpdate && (
                          <td className='align-middle small'>
                            <button
                              type='button'
                              className='btn btn-outline-danger w-100'
                              onClick={() => handleDeleteLine(Index)}
                            >
                              <i className='fas fa-trash-alt'></i>
                            </button>
                          </td>
                        )}

                        {isUpdate && line.id !== null && (
                          <td className='align-middle small text-end'>
                            <button
                              disabled={rowLoading[Index]}
                              type='button'
                              className='btn   btn-outline-theme w-100'
                              onClick={() => handleDeleteAllocateLine(line, Index)}
                            >
                              {rowLoading[Index] ? (
                                <div className='spinner-border spinner-border-sm me-2'></div>
                              ) : (
                                <i className='fas fa-unlock me-2'></i>
                              )}
                              فك الحجز
                            </button>
                          </td>
                        )}

                        {isUpdate && line.id === null && (
                          <td className='align-middle small text-end'>
                            <div className='btn-group w-100'>
                              <button
                                type='button'
                                className='btn   btn-outline-danger me-1 '
                                onClick={() => handleDeleteLine(Index)}
                              >
                                {/* <i className='fa fa-power-off fa-fw me-1'></i> */}
                                {t('Cancel')}
                              </button>
                              <button
                                // disabled={isLoading}
                                disabled={rowLoading[Index]}
                                type='button'
                                className='btn   btn-outline-success '
                                onClick={() => handleAddAllocateLine(line, Index)}
                              >
                                {rowLoading[Index] ? (
                                  <div className='spinner-border spinner-border-sm me-2'></div>
                                ) : (
                                  // <i className='fas fa-lock me-2'></i>
                                  <></>
                                )}
                                حجز
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
              </tbody>
            </table>
            {formik.values.materialTransactionsLineList?.length === 0 && (
              <div className='text-center py-5 bg-secondary bg-opacity-50 '>
                <i className='bi bi-inbox display-4 text-muted'></i>
                <p className='mt-3 text-muted'>{t('لا توجد أصناف مضافة')}</p>

                <button type='button' className='btn btn-theme mt-2' onClick={handleAddLine}>
                  <i className='fas fa-plus me-2'></i>
                  {t('إضافة اول صنف')}
                </button>
              </div>
            )}
          </div>
        </div>
      </Loading>
    </>
  );
};

export default AllocateLines;
